import { useEffect, useRef } from "react";
import type { KeyboardEvent as ReactKeyboardEvent, RefObject } from "react";

// Everything a keyboard user can land on inside a dialog.
const FOCUSABLE_SELECTOR =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

function isKeyboardReachable(element: HTMLElement, root: HTMLElement): boolean {
  let current: HTMLElement | null = element;
  while (current) {
    if (
      current.hidden ||
      current.hasAttribute("inert") ||
      current.getAttribute("aria-hidden") === "true"
    ) {
      return false;
    }
    const style = window.getComputedStyle(current);
    if (style.display === "none" || style.visibility === "hidden") return false;
    if (current === root) break;
    current = current.parentElement;
  }
  return true;
}

function getFocusableElements(root: HTMLElement): HTMLElement[] {
  return Array.from(
    root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
  ).filter((element) => isKeyboardReachable(element, root));
}

interface UseDialogKeyboardOptions {
  /** Whether the dialog is currently open. */
  open: boolean;
  /** Called when the user presses Escape. */
  onClose: () => void;
  /** Ref to the dialog's content container — the focus-trap boundary. */
  dialogRef: RefObject<HTMLElement | null>;
  /**
   * Optional CSS selector (queried inside the dialog) for the element that
   * should receive focus when the dialog opens. Falls back to the first
   * focusable element. Skipped entirely when focus is already inside the
   * dialog (e.g. an autoFocus input beat us to it).
   */
  initialFocus?: string;
}

/**
 * Keyboard accessibility for hand-rolled portaled dialogs (the pattern
 * extracted from the avatar looks modal in AvatarLibrary.tsx):
 *
 * - Escape closes the dialog.
 * - Tab / Shift+Tab wrap focus inside the dialog (focus trap).
 * - On open, focus moves into the dialog; on close, focus returns to the
 *   element that was focused when the dialog opened (usually the trigger).
 *
 * Returns a keydown handler to attach to the dialog's OVERLAY element (the
 * outermost portaled div) so it catches keys from anywhere inside. Nested
 * overlays (CustomDropdown, a stacked ConfirmDialog, …) that handle Escape
 * themselves must preventDefault/stopPropagation so only the topmost layer
 * closes — this handler ignores already-handled events.
 */
export function useDialogKeyboard({
  open,
  onClose,
  dialogRef,
  initialFocus,
}: UseDialogKeyboardOptions): (e: ReactKeyboardEvent) => void {
  // Keep the latest onClose without re-running the open/close effect.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const restoreRef = useRef<HTMLElement | null>(null);

  // On open: remember the trigger, move focus into the dialog.
  // On close/unmount: return focus to the trigger.
  useEffect(() => {
    if (!open) return;
    restoreRef.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const root = dialogRef.current;
    if (root && !root.contains(document.activeElement)) {
      const requestedTarget = initialFocus
        ? root.querySelector<HTMLElement>(initialFocus)
        : null;
      const target =
        requestedTarget && isKeyboardReachable(requestedTarget, root)
          ? requestedTarget
          : getFocusableElements(root)[0];
      target?.focus();
    }
    return () => {
      const el = restoreRef.current;
      restoreRef.current = null;
      if (el && document.contains(el)) el.focus();
    };
    // dialogRef is a stable ref object; initialFocus is a constant selector.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (e: ReactKeyboardEvent) => {
    // A nested overlay (dropdown, stacked dialog) already handled this key.
    if (e.defaultPrevented) return;
    if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      onCloseRef.current();
      return;
    }
    if (e.key !== "Tab") return;
    const root = dialogRef.current;
    if (!root) return;
    const focusables = getFocusableElements(root);
    if (focusables.length === 0) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = document.activeElement;
    if (e.shiftKey) {
      if (active === first || !root.contains(active)) {
        e.preventDefault();
        e.stopPropagation();
        last.focus();
      }
    } else if (active === last || !root.contains(active)) {
      e.preventDefault();
      e.stopPropagation();
      first.focus();
    }
  };
}
